import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledMultiselect } from 'src/components/form/controlled-multiselect/ControlledMultiselect';
import ControlledSelect from 'src/components/form/controlled-select';
import { ControlledSwitch } from 'src/components/form/controlled-switch/ControlledSwitch';

import type { SupportAccessStatus } from '../supportAccess';
import type {
  UserDetailsFormFields,
  UserDetailsFormFieldsMultiRole,
} from './UserDetailsSchema';

interface Props {
  roleOptions: {
    id: string;
    value: string;
    description?: string;
  }[];
  readOnly?: boolean;
  multiRoleMode?: boolean;
  /**
   * COI-819 — render the support-entitlement field. The caller decides; see
   * `supportAccess.ts`. The server gates the write independently.
   */
  showSupportAccess?: boolean;
  /**
   * Render it read-only. In practice this means an `inert` target — a write the
   * DB's two-factor rule would refuse — since the field only renders for a
   * CustomerSupport caller now. The non-CS half of `canEditSupportAccess` is
   * kept as defence in depth, not as a state the UI expects to reach.
   */
  supportAccessReadOnly?: boolean;
  /**
   * Drives the help text. `inert` is the state worth spelling out: the
   * membership role says support and the DB's second factor is missing, so it
   * confers nothing.
   */
  supportAccessStatus?: SupportAccessStatus;
}

const UserDetailsForm: FC<Props> = ({
  roleOptions,
  readOnly,
  multiRoleMode,
  showSupportAccess,
  supportAccessReadOnly,
  supportAccessStatus,
}) => {
  const { control } = useFormContext<
    UserDetailsFormFields | UserDetailsFormFieldsMultiRole
  >();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'userSettings',
  });

  // Convert roleOptions to format expected by ControlledMultiselect
  const multiselectRoleOptions = roleOptions.map((role) => ({
    label: role.value,
    value: role.id,
    description: role.description,
  }));

  return (
    <>
      {multiRoleMode ? (
        <ControlledMultiselect
          forceRequired={true}
          label={t('fields.roles')}
          name={'Roles'}
          description={t('fields.roles_help')}
          control={control}
          options={multiselectRoleOptions}
          disabled={readOnly}
        />
      ) : (
        <ControlledSelect
          forceRequired={true}
          key={'type'}
          label={t('fields.role')}
          name={'Role'}
          description={t('fields.role_help')}
          placeholder={t('fields.role_placeholder')}
          control={control}
          testId={'role'}
          options={roleOptions}
          disabled={readOnly}
        />
      )}
      {showSupportAccess && (
        <ControlledSwitch
          key={'SupportAccess'}
          testId={'supportAccess'}
          label={t('fields.supportAccess')}
          description={
            supportAccessStatus === 'inert'
              ? t('fields.supportAccess_inert_help')
              : t('fields.supportAccess_help')
          }
          name={'SupportAccess'}
          control={control}
          disabled={readOnly || supportAccessReadOnly}
        />
      )}
    </>
  );
};

export default UserDetailsForm;
